// The map wraps around: past the top edge of the islands the bottom ones come
// into view (and so on for every side), as if the ocean were a globe. The world
// is drawn once more at a whole number of periods away wherever such a copy
// would be on screen, and when the camera drifts more than half a period away
// it is moved back by one period — the picture does not change, so the jump is
// invisible. Pure geometry here; the PIXI side is worldWrap.ts.

export interface Rect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface WrapPeriod {
  /** Everything that is drawn: islands and the objects on them. */
  content: Rect;
  /** Horizontal / vertical distance between two copies of the world. */
  px: number;
  py: number;
}

/** Open ocean between the edge of one copy and the next, in world units. */
export const WRAP_GAP = 900;

export function wrapPeriod(content: Rect, gap = WRAP_GAP): WrapPeriod {
  return {
    content,
    px: content.maxX - content.minX + gap,
    py: content.maxY - content.minY + gap,
  };
}

/**
 * How far to move the camera so its centre stays within half a period of the
 * world's centre (0 when it already does). Always a whole number of periods.
 */
export function cameraWrapShift(center: { x: number; y: number }, p: WrapPeriod): { dx: number; dy: number } {
  const cx = (p.content.minX + p.content.maxX) / 2;
  const cy = (p.content.minY + p.content.maxY) / 2;
  // `+ 0` turns -0 into 0.
  return {
    dx: -Math.round((center.x - cx) / p.px) * p.px + 0,
    dy: -Math.round((center.y - cy) / p.py) * p.py + 0,
  };
}

/** World offsets of the extra copies that overlap the visible area (never [0, 0]). */
export function ghostOffsets(view: Rect, p: WrapPeriod): Array<[number, number]> {
  const { content, px, py } = p;
  // Copy i is at content + i*px: it is visible when it overlaps the view.
  const iFrom = Math.ceil((view.minX - content.maxX) / px);
  const iTo = Math.floor((view.maxX - content.minX) / px);
  const jFrom = Math.ceil((view.minY - content.maxY) / py);
  const jTo = Math.floor((view.maxY - content.minY) / py);
  const offsets: Array<[number, number]> = [];
  for (let i = iFrom; i <= iTo; i++) {
    for (let j = jFrom; j <= jTo; j++) {
      if (i !== 0 || j !== 0) offsets.push([i * px + 0, j * py + 0]);
    }
  }
  return offsets;
}

/**
 * The areas of the real world that are on screen — the view itself plus, for
 * each copy, the part of the world that copy shows. Culling keeps an object
 * drawn when it falls in any of them.
 */
export function visibleWorldRects(view: Rect, p: WrapPeriod | null): Rect[] {
  if (!p) return [view];
  return [view, ...ghostOffsets(view, p).map(([dx, dy]) => ({
    minX: view.minX - dx,
    maxX: view.maxX - dx,
    minY: view.minY - dy,
    maxY: view.maxY - dy,
  }))];
}

export function rectContains(r: Rect, x: number, y: number): boolean {
  return x >= r.minX && x <= r.maxX && y >= r.minY && y <= r.maxY;
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.maxX >= b.minX && a.minX <= b.maxX && a.maxY >= b.minY && a.minY <= b.maxY;
}
